import {
  GroupedSizeFilterSection,
  GroupedSizeFilterSection_SizeRun,
  GroupedSizeFilterSection_SizeRunCluster,
  GroupedSizeFilterSection_SizeRunClusters,
  GroupedSizeFilterSection_SizeTab,
  GroupedSizeFilterSection_SizeTabs,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {useMemo} from 'react';
import {useSizeFiltersSlugs} from './useSizeFiltersSlugs';

export const useGroupedSizeFilterSection = ({
  data,
  setFilter,
  resetFilters,
}: {
  data: GroupedSizeFilterSection;
  setFilter: (filterData: {
    filterId: string;
    filterSection: string;
    singleSelectOnly?: boolean;
  }) => void;
  resetFilters: (slug: string[]) => void;
}) => {
  const {content} = data;
  const possibleSizeFilterSlugs = useSizeFiltersSlugs(data);
  const normalizedContent = useMemo(() => normalizeContent(content), [content]);

  const handleSetSizeFilter = (slug: string, sizeId: string) => {
    setFilter({
      filterId: sizeId,
      filterSection: slug,
    });
  };

  const handleTabClick = (slug: string, sizeId: string) => {
    setFilter({
      filterId: sizeId,
      filterSection: slug,
      singleSelectOnly: true,
    });
  };

  const handleResetFilter = () => {
    resetFilters(possibleSizeFilterSlugs);
  };

  return {
    normalizedContent,
    handleSetSizeFilter,
    handleTabClick,
    handleResetFilter,
  };
};

const normalizeSizeRuns = (sizeRuns: GroupedSizeFilterSection_SizeRun[]) => {
  return sizeRuns.map(sizeRun => {
    return {
      label: sizeRun.label,
      sizes: sizeRun.sizes.map(size => ({id: `${size.id}`, label: size.label})),
      slug: sizeRun.slug,
    };
  });
};

const normalizeSizeClusters = (
  clusters?: GroupedSizeFilterSection_SizeRunCluster[],
) => {
  if (!clusters) {
    return undefined;
  }

  return clusters.map(cluster => {
    return {
      label: cluster.label,
      sizeRuns: normalizeSizeRuns(cluster.sizeRuns),
    };
  });
};

const normalizeSizeTabs = (tabs: GroupedSizeFilterSection_SizeTab[]) => {
  return tabs.map(tab => {
    return {
      clusters: normalizeSizeClusters(tab.sizeRunClusters?.clusters),
      label: tab.label,
      specialSizeFilter: {
        filterValueId: tab.specialSizeFilter?.filterValueId
          ? `${tab.specialSizeFilter?.filterValueId}`
          : undefined,
        slug: tab.specialSizeFilter?.slug,
      },
    };
  });
};

const normalizeContent = (
  sizeTabsOrClusters:
    | {
        $case: 'sizeTabs';
        sizeTabs: GroupedSizeFilterSection_SizeTabs;
      }
    | {
        $case: 'sizeRunClusters';
        sizeRunClusters: GroupedSizeFilterSection_SizeRunClusters;
      }
    | undefined,
) => {
  if (sizeTabsOrClusters?.$case === 'sizeRunClusters') {
    return {
      clusters: normalizeSizeClusters(
        sizeTabsOrClusters.sizeRunClusters.clusters,
      ),
    };
  }

  if (sizeTabsOrClusters?.$case === 'sizeTabs') {
    return {
      tabs: normalizeSizeTabs(sizeTabsOrClusters.sizeTabs.tabs),
    };
  }

  return undefined;
};
